import { type FC, type ReactNode, useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import styles from './CalendarSyncSettingsView.module.scss'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { Button } from '../../../../components/Button/Button'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../common/integrations'
import { type CalendarAttendeeSourceConfig, CalendarAttendeeSourceField } from './CalendarAttendeeSourceField'

interface CalendarSyncFormValues {
  enabled: boolean
  attendeeSource: string | null
}

const defaultValues: CalendarSyncFormValues = { enabled: false, attendeeSource: null }

const UPDATE_SCOPE = { scope: 'integration:update', superScope: ['integration:admin'] }

export interface CalendarSyncSettingsViewProps {
  serviceId: IntegrationServiceId
  notice?: ReactNode
  noticeVariant?: 'info' | 'warning'
  enabled: boolean | null
  disabled?: boolean
  isLoading: boolean
  isSaving: boolean
  attendeeSource?: CalendarAttendeeSourceConfig
  onSave: (enabled: boolean, attendeeSource: string | null) => Promise<boolean>
}

export const CalendarSyncSettingsView: FC<CalendarSyncSettingsViewProps> = ({
  serviceId,
  notice,
  noticeVariant = 'info',
  enabled,
  disabled = false,
  isLoading,
  isSaving,
  attendeeSource,
  onSave,
}) => {
  const storedAttendeeSource = attendeeSource?.value ?? null
  const serverValues = useMemo(() => ({ enabled: enabled === true, attendeeSource: storedAttendeeSource }), [enabled, storedAttendeeSource])
  const form = useForm<CalendarSyncFormValues>({ defaultValues, values: serverValues })
  const enabledValue = useWatch({ control: form.control, name: 'enabled' })

  const onSubmit = async (data: CalendarSyncFormValues) => {
    const saved = await onSave(data.enabled, data.attendeeSource)
    if (!saved) {
      form.reset()
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  if (isLoading) {
    return (
      <Container>
        <div className={classNames('skeleton w-100 mb-2', styles.skeletonRow)} />
        <div className={classNames('skeleton w-100 mb-3', styles.skeletonRow)} />
      </Container>
    )
  }

  return (
    <Container className='pb-3'>
      <h6>Calendar</h6>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='vertical' className='gap-4 pb-4 pb-md-0'>
            <div>
              Enable calendar synchronization if you use your {getIntegrationDisplayName(serviceId)} calendar to track meetings. Events will sync to Zocks, and
              your AI Assistant can join meetings automatically.
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='enabled'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={UPDATE_SCOPE}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={disabled} />
                  {fieldState.error ? <FormFeedback>{fieldState.error.message}</FormFeedback> : null}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      {notice ? (
        <Row className='mb-1 mt-4'>
          <Col>
            <div className={classNames('alert small', { 'alert-info': noticeVariant === 'info', 'alert-warning': noticeVariant === 'warning' })}>{notice}</div>
          </Col>
        </Row>
      ) : null}
      <hr />
      {attendeeSource && enabledValue ? (
        <>
          <Row className='mb-1 mt-3'>
            <Col>
              <Controller
                name='attendeeSource'
                control={form.control}
                render={({ field }) => (
                  <CalendarAttendeeSourceField
                    serviceId={serviceId}
                    options={attendeeSource.options}
                    note={attendeeSource.note}
                    value={field.value}
                    disabled={disabled}
                    onChange={field.onChange}
                  />
                )}
              />
            </Col>
          </Row>
          <hr />
        </>
      ) : null}
      <Row>
        <Col>
          <div className='paragraph-medium zocks-text-gray-600'>
            To ensure your AI Assistant joins all relevant meetings in your calendar, configure filtering and auto-join options on the{' '}
            <Link className='button-link-medium zocks-text-primary cursor-pointer' to='/account/profile-settings/meeting-assistant'>
              Event Filters
            </Link>{' '}
            page.
          </div>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isSaving}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={UPDATE_SCOPE}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isSaving}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
